import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Download, FileText, Eye, RefreshCw, Send, Bell } from 'lucide-react';
import { API_BASE_URL, getAuthHeaders, generateEdi849Preview, downloadEdiDocument, getPriceNotifications, communicatePricing } from '../../config/api';

// Normalize a paginated/wrapped list response into an array.
const asList = (data, keys) => {
  const d = data && data.data !== undefined ? data.data : data;
  if (Array.isArray(d)) return d;
  for (const k of keys) if (d && Array.isArray(d[k])) return d[k];
  if (d && d.data && Array.isArray(d.data)) return d.data;
  return [];
};

const OutboundEDI = () => {
  const [batches, setBatches] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [contractId, setContractId] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [preview, setPreview] = useState(null);
  const [pcContractId, setPcContractId] = useState('');
  const [notifications, setNotifications] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [b, c] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/batches?limit=50`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE_URL}/api/contracts?limit=200`, { headers: getAuthHeaders() })
      ]);
      setBatches(asList(b.data, ['batches']));
      setContracts(asList(c.data, ['contracts']));
      try { setNotifications(await getPriceNotifications()); } catch (e) { /* non-fatal */ }
    } catch (e) {
      toast.error('Failed to load batches/contracts');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const communicate = async () => {
    if (!pcContractId) { toast.error('Select a contract'); return; }
    setBusy('communicate');
    try {
      const res = await communicatePricing(pcContractId);
      toast.success(`Pricing communicated to ${res.customersNotified} customer(s)`);
      setNotifications(await getPriceNotifications());
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to communicate pricing');
    } finally { setBusy(null); }
  };

  const download849 = async (batch) => {
    setBusy(`849-${batch.id}`);
    try { await downloadEdiDocument('849', batch.id, `849_${batch.batchNumber || batch.id}.edi`); toast.success('EDI 849 downloaded'); }
    catch (e) { toast.error(e.response?.data?.error || 'Nothing to respond to in this batch'); }
    finally { setBusy(null); }
  };

  const preview849 = async (batch) => {
    setBusy(`prev-${batch.id}`);
    try { const doc = await generateEdi849Preview(batch.id); setPreview({ title: doc.filename, content: doc.content }); }
    catch (e) { toast.error(e.response?.data?.error || 'Cannot preview 849 for this batch'); }
    finally { setBusy(null); }
  };

  const priceCountOf = (c) => (c && c._count && typeof c._count.prices === 'number') ? c._count.prices : null;
  const selectedContract = contracts.find(c => c.id === contractId);
  const selectedPriceCount = priceCountOf(selectedContract);
  const contractsWithPrices = contracts.filter(c => (priceCountOf(c) ?? 0) > 0).length;

  const download845 = async () => {
    if (!contractId) { toast.error('Select a contract'); return; }
    setBusy('845');
    try { await downloadEdiDocument('845', contractId, `845_${contractId}.edi`); toast.success('EDI 845 downloaded'); }
    catch (e) { toast.error(e.response?.data?.error || 'Contract has no prices to authorize'); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-6">
      {/* 849 — Chargeback responses */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">EDI 849 — Chargeback Responses</h3>
            <p className="text-sm text-gray-500">Generate the X12 849 response (accept/reject per line) for an adjudicated batch and send it to the wholesaler.</p>
          </div>
          <button onClick={load} title="Refresh" className="inline-flex items-center px-3 py-2 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50"><tr>
              {['Batch', 'Status', 'Records', 'Actions'].map(h => <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>)}
            </tr></thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan={4} className="px-6 py-6 text-center text-sm text-gray-500">Loading…</td></tr>
              ) : batches.length === 0 ? (
                <tr><td colSpan={4} className="px-6 py-6 text-center text-sm text-gray-500">No batches yet. Process an inbound 844 first.</td></tr>
              ) : batches.map(b => {
                const claimCount = b.recordCount ?? b._count?.claims ?? 0;
                const noClaims = !claimCount;
                return (
                <tr key={b.id} className="hover:bg-gray-50">
                  <td className="px-6 py-3 text-sm font-medium text-gray-900">{b.batchNumber || b.id}</td>
                  <td className="px-6 py-3 text-sm text-gray-500">{b.status}</td>
                  <td className="px-6 py-3 text-sm text-gray-500">{b.recordCount ?? b._count?.claims ?? '—'}</td>
                  <td className="px-6 py-3 text-sm space-x-3">
                    <button disabled={busy === `prev-${b.id}` || noClaims} onClick={() => preview849(b)} title={noClaims ? 'This batch has no claims to respond to' : 'Preview'} className="inline-flex items-center text-gray-600 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed"><Eye className="h-4 w-4 mr-1" />Preview</button>
                    <button disabled={busy === `849-${b.id}` || noClaims} onClick={() => download849(b)} title={noClaims ? 'This batch has no claims to respond to' : 'Download 849'} className="inline-flex items-center text-primary-600 hover:text-primary-800 disabled:opacity-40 disabled:cursor-not-allowed"><Download className="h-4 w-4 mr-1" />Download 849</button>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      </div>

      {/* 845 — Price authorization */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">EDI 845 — Price Authorization</h3>
          <p className="text-sm text-gray-500">Generate the X12 845 price authorization for a contract (all priced products with effective windows).</p>
        </div>
        <div className="px-6 py-4 flex items-end space-x-3">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Contract</label>
            <select value={contractId} onChange={e => setContractId(e.target.value)} className="w-full border-gray-300 rounded-md text-sm">
              <option value="">Select a contract…</option>
              {contracts.map(c => {
                const n = priceCountOf(c);
                const label = `${c.number || c.contractName || c.id}${c.description ? ` — ${c.description}` : ''}`;
                const suffix = n === null ? '' : (n === 0 ? '  (no prices)' : `  (${n} price${n === 1 ? '' : 's'})`);
                return <option key={c.id} value={c.id} disabled={n === 0}>{label}{suffix}</option>;
              })}
            </select>
          </div>
          <button disabled={busy === '845' || !contractId || selectedPriceCount === 0} onClick={download845} title="Download 845" className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed">
            <Download className="h-4 w-4 mr-2" /> Download 845
          </button>
        </div>
        {!loading && contracts.length > 0 && contractsWithPrices === 0 && (
          <div className="mx-6 mb-4 rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
            None of your contracts have prices yet, so there is nothing to authorize in an 845. Add contract prices under
            {' '}<span className="font-medium">Contracts → open a contract → Pricing</span>, then return here.
          </div>
        )}
        {!loading && selectedContract && selectedPriceCount === 0 && contractsWithPrices > 0 && (
          <div className="mx-6 mb-4 rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
            <span className="font-medium">{selectedContract.number || selectedContract.id}</span> has no prices. Pick a contract that shows a price count, or add prices under its Pricing tab.
          </div>
        )}
      </div>

      {/* Pricing change communication (Sales / Account Manager) */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Pricing Change Communication</h3>
          <p className="text-sm text-gray-500">Push a contract's current pricing to its rostered customers as EDI 845 notifications — so pricing changes and new products reach operations before claims are rejected for stale pricing.</p>
        </div>
        <div className="px-6 py-4 flex items-end space-x-3">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Contract</label>
            <select value={pcContractId} onChange={e => setPcContractId(e.target.value)} className="w-full border-gray-300 rounded-md text-sm">
              <option value="">Select a contract…</option>
              {contracts.map(c => {
                const n = priceCountOf(c);
                return <option key={c.id} value={c.id} disabled={n === 0}>{c.number || c.contractName || c.id}{n === null ? '' : (n === 0 ? '  (no prices)' : `  (${n} prices)`)}</option>;
              })}
            </select>
          </div>
          <button disabled={busy === 'communicate' || !pcContractId} onClick={communicate} title="Communicate Pricing" className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed">
            <Send className="h-4 w-4 mr-2" /> Communicate Pricing
          </button>
        </div>
        <div className="overflow-x-auto border-t border-gray-100">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50"><tr>
              {['Customer', 'Mode', 'Products', 'Communicated'].map(h => <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>)}
            </tr></thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {notifications.length === 0 ? (
                <tr><td colSpan={4} className="px-6 py-6 text-center text-sm text-gray-500"><Bell className="h-6 w-6 mx-auto text-gray-300 mb-1" />No pricing communicated yet.</td></tr>
              ) : notifications.slice(0, 25).map(n => (
                <tr key={n.id} className="hover:bg-gray-50">
                  <td className="px-6 py-3 text-sm font-medium text-gray-900">{n.customer?.name || n.customerId}</td>
                  <td className="px-6 py-3 text-sm text-gray-500">{n.mode}</td>
                  <td className="px-6 py-3 text-sm text-gray-500">{n._count?.entries ?? n.totalChanges ?? '—'}</td>
                  <td className="px-6 py-3 text-sm text-gray-500">{n.notificationDate ? new Date(n.notificationDate).toLocaleString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* X12 preview */}
      {preview && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center"><FileText className="h-5 w-5 text-primary-600 mr-2" /><h3 className="text-lg font-medium text-gray-900">{preview.title}</h3></div>
            <button onClick={() => setPreview(null)} title="Close" className="text-sm text-gray-500 hover:text-gray-700">Close</button>
          </div>
          <pre className="px-6 py-4 text-xs text-gray-800 whitespace-pre-wrap break-all max-h-96 overflow-y-auto font-mono">{(preview.content || '').replace(/~/g, '~\n')}</pre>
        </div>
      )}
    </div>
  );
};

export default OutboundEDI;
